import { useLocation } from "react-router-dom";
import { useEffect, useRef, useCallback } from "react";
import { PermissionTypes } from 'constants/enums';
import { selectCurrentMe } from 'data/CurrentUser/selectors';
import { getState } from 'pluginServices/store';
import projectService from 'services/ProjectService';
import ActivityResolver from 'resolvers/RTMResolvers/ActivityResolver';

const { REVIEWER } = PermissionTypes;

const RouterMiddleware = ({ children, setReviewerBanner, connection }) => {
  const location = useLocation();
  const connectionRef = useRef(null);

  const getCookie = (name) => document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.split('=')[1];

  const extractIdFromUrl = (url, type) => {
    const [, id] = url.split(`${type}/`);
    return id?.split("/")[0] || null;
  };

  const getCourseOrLessonId = useCallback(() => {
    const path = location.pathname;
    return path.includes("course/")
      ? extractIdFromUrl(path, "course")
      : extractIdFromUrl(path, "lesson");
  }, [location.pathname]);


  const fetchProjectId = async (connection, courseId) => {
    const activityResolver = new ActivityResolver(connection);
    const { projectId } = await activityResolver.authorActivityBreadcrumb({
      elementId: courseId,
      elementType: "ACTIVITY",
    });
    return projectId;
  };

  const fetchCollaborators = async (projectId) => {
    const collaborators = await projectService.getProjectCollaboratorSummary(projectId);
    return { ...collaborators };
  };

  const getCurrentUserAccId = async () => {
    const { accountId } = selectCurrentMe(getState());
    return accountId;
  };

  const checkReviewerPermission = useCallback((loggedInAccId, collaborators) => {
    const permissionLevel = collaborators.accounts.find(
      (collab) => collab.account.accountId === loggedInAccId)?.permissionLevel;
    if (permissionLevel === REVIEWER) {
      setReviewerBanner(true);
    }
  }, [setReviewerBanner]);

  useEffect(() => {
    const processCollaborators = async () => {
      const bearerToken = getCookie("bearerToken");
      if (!bearerToken) return;

      try {
        const courseIdOrLessonId = getCourseOrLessonId();
        if (courseIdOrLessonId) {
          const projectId = await fetchProjectId(connection, courseIdOrLessonId);
          const collaborators = await fetchCollaborators(projectId);
          if (collaborators) {
            const currentUserAccId = await getCurrentUserAccId();
            if (currentUserAccId) {
              checkReviewerPermission(currentUserAccId, collaborators);
            }
          }
        }
      } catch (error) {
        setReviewerBanner(false);
      }
    };
    // set the flag as false for all the cases initially
    setReviewerBanner(false);

    const shouldShowBanner = (location.pathname.includes("course/") || location.pathname.includes("lesson/")) && !location.pathname.includes("preview/");
    if (shouldShowBanner)
      processCollaborators();

    return () => {
      connectionRef.current?.shutDown();
      connectionRef.current = null;
    };
  }, [location.pathname, getCourseOrLessonId, setReviewerBanner, checkReviewerPermission]);

  return children || null;
};

export default RouterMiddleware;